import { useCallback, useRef, useState } from "react";
import { GstRestS } from "../../rest/gst-rest-service";
import { useGstReconSocket } from "./useGstReconSocket";

export type SyncPhase = "idle" | "waiting" | "connected" | "synced" | "failed";

export type TallyTrackEvent =
  | "tally_tdl_downloaded"
  | "tally_tdl_not_downloaded"
  | "tally_connected"
  | "tally_synced"
  | "tally_sync_failed";

export type TallyTrackFn = (
  event: TallyTrackEvent,
  opts: { success: boolean; error_code?: string; error_message?: string }
) => void;

// Shared by every Tally-connect consumer (GST Reconciliation's full-page setup
// flow, its dashboard reconnect modal, and Lending's Financials step) — all of
// them hit the same company-scoped backend endpoints/socket event, so only the
// analytics callback varies per caller. `onTrack` is intentionally generic
// (not `trackGstProgressUpdated` directly) since that function hardcodes
// `feature: "gst_recon"`, which would mislabel events fired from Lending.
export function useTallyConnectionFlow(initialPhase: SyncPhase = "idle", onTrack?: TallyTrackFn) {
  const [phase, setPhase] = useState<SyncPhase>(initialPhase);
  const [error, setError] = useState<string | null>(null);

  // Keep onTrack in a ref so downloadTdl/checkStatus/retrySync don't change
  // identity when the caller passes an inline arrow function (which would
  // otherwise cause useEffects depending on checkStatus to fire every render).
  const onTrackRef = useRef(onTrack);
  onTrackRef.current = onTrack;

  const downloadTdl = useCallback(async () => {
    try {
      const res = await GstRestS.getTallyTdl();
      onTrackRef.current?.("tally_tdl_downloaded", { success: true });
      window.location.href = res.data.download_url;
      return res;
    } catch (err: any) {
      onTrackRef.current?.("tally_tdl_not_downloaded", { success: false, error_message: err?.message });
      throw err;
    }
  }, []);

  const checkStatus = useCallback(async () => {
    const res = await GstRestS.getJobStatus();
    const tallyConnection = res.data.tallyAppConnection;
    const isConnected = Boolean(
      tallyConnection?.tdlDownloaded && tallyConnection?.tallyConnected
    );

    if (isConnected) {
      setPhase((prev) => {
        if (prev !== "connected") {
          onTrackRef.current?.("tally_connected", { success: true });
        }
        return "connected";
      });
    } else if (tallyConnection?.tdlDownloaded && tallyConnection?.tallySyncFailed) {
      // The connector was installed but a previous sync attempt failed —
      // seed "failed" so TallySyncInstall shows its troubleshooting card
      // without waiting for a live socket event. Mirrors the resume behavior
      // the GST full-page flow sets up via TallySyncFlowLayout's
      // `resumedFailed` seed and the GST dashboard modal's
      // openTallyDashboardModal() tallySyncFailed handling.
      setPhase((prev) => (prev === "failed" ? prev : "failed"));
    }

    return {
      success: res.success,
      data: {
        synced: isConnected,
        tallyConnection: tallyConnection ?? null,
      },
    };
  }, []);

  const retrySync = useCallback(async () => {
    const res = await GstRestS.retryTallySync();
    const synced = res.data.synced;
    onTrackRef.current?.(synced ? "tally_synced" : "tally_sync_failed", { success: synced });
    setPhase(synced ? "synced" : "failed");
    return res;
  }, []);

  useGstReconSocket((update) => {
    if (update.jobType !== "tally_push") return;

    // Once a sync has completed, don't let stale connected/failed events
    // revert the phase — mirrors TallyDashboardSyncModal's guard that only
    // allows "completed" through after stage === "success".
    if (update.status === "connected") {
      setPhase((prev) => {
        if (prev === "synced") return prev;
        if (prev !== "connected") {
          onTrackRef.current?.("tally_connected", { success: true });
        }
        return "connected";
      });
    }
    if (update.status === "completed") {
      setPhase((prev) => {
        if (prev !== "synced") {
          onTrackRef.current?.("tally_synced", { success: true });
        }
        return "synced";
      });
    }
    if (update.status === "failed") {
      setPhase((prev) => {
        if (prev === "synced") return prev;
        onTrackRef.current?.("tally_sync_failed", { success: false, error_code: update.error?.code });
        return "failed";
      });
      setError(update.error?.message ?? null);
    }
  });

  return { phase, error, downloadTdl, checkStatus, retrySync };
}

export type TallyConnectionFlowResult = ReturnType<typeof useTallyConnectionFlow>;
